/* @flow */

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import type { ParentalControlAndETag } from '../../../helpers/parentalControl/constsAndTypes';
import type { Profile, Profiles } from '../../../helpers/profiles/typesAndConstants';
import type { CONFIRMATION_DATA_MODAL_TYPE } from '../confirmationModal/ConfirmationModal';

import { fireAndForget } from '../../../helpers/jsHelpers/promise';
import Localizer from '../../../helpers/localization';
import { Messenger, MessengerEvents } from '../../../helpers/messenger';
import ParentalControlManager from '../../../helpers/parentalControl/parentalControlManager';
import { isProfilePrivate } from '../../../helpers/profiles/helpers';
import ProfilesManager from '../../../helpers/profiles/profilesManager';
import { Setting } from '../../../helpers/settings/types';
import { showConfirmationModal, showPinModal } from '../../../redux/modal/actions';
import { sendParentalControlUpdateRequest } from '../../../redux/netgemApi/actions/personalData/parentalControl';
import { PictoKid, PictoLock } from '../../pictos/Element';
import Avatar from '../../profiles/Avatar';
import ProfileTypeIndicator, { ProfileType } from '../../profiles/ProfileTypeIndicator';
import { AvatarSize, DisplayNamePlacement } from '../../profiles/types';
import RowSettingAction from '../../settings/RowSettingAction';
import RowSettingToggle from '../../settings/RowSettingToggle';
import { ConfirmationModalResult } from '../confirmationModal/ConfirmationModal';
import Modal from '../modal';
import './ParentalControlModal.css';
import { PinModalKind } from '../pinModal/PinModalConstsAndTypes';
import SharedProfileSettings from '../profileEditorModal/SharedProfileSettings';
import RestrictionSelector from './RestrictionSelector';

export enum ParentalControlKind {
  Account,
  Device,
}

enum ParentalControlModalState {
  Game,
  Main,
  Profile,
  Video,
}

// eslint-disable-next-line max-lines-per-function
const ParentalControlModal = (): React.Node => {
  const dispatch = useDispatch();

  const abortController = useRef<AbortController>(new AbortController());

  const [isParentalControlEnabled, setIsParentalControlEnabled] = useState<boolean>(ParentalControlManager.isLocked());
  const [visualState, setVisualState] = useState<ParentalControlModalState>(ParentalControlModalState.Main);
  const [profiles, setProfiles] = useState<Profiles | null>(null);
  const [currentProfile, setCurrentProfile] = useState<Profile | null>(null);
  const [isWorking, setIsWorking] = useState<boolean>(false);
  const [accountVideoRestrictionHint, setAccountVideoRestrictionHint] = useState<string>('');
  const [currentRestrictionKind, setCurrentRestrictionKind] = useState<ParentalControlKind>(ParentalControlKind.Account);
  const [deviceVideoRestrictionHint, setDeviceVideoRestrictionHint] = useState<string>('');
  const [hasDeviceRestrictions, setHasDeviceRestrictions] = useState<boolean>(ParentalControlManager.hasDeviceRestrictions);
  const [isAccountLockPurchaseEnabled, setIsAccountLockPurchaseEnabled] = useState<boolean>(ParentalControlManager.accountLockPurchase);
  const [isDeviceLockPurchaseEnabled, setIsDeviceLockPurchaseEnabled] = useState<boolean>(ParentalControlManager.deviceLockPurchase);

  const updateProfiles = useCallback(async () => {
    const profiles = await ProfilesManager.getProfiles(true);
    setProfiles(profiles);

    // Check if current profile still exists
    if (currentProfile !== null && !profiles.list.find(({ id }) => id === currentProfile.id)) {
      setVisualState(ParentalControlModalState.Main);
      setCurrentProfile(null);
    }
  }, [currentProfile]);

  useEffect(() => {
    if (ProfilesManager.isEnabled) {
      fireAndForget(updateProfiles(), 'ParentalControlModal.loadProfiles');
    }
  }, [updateProfiles]);

  const updateParentalControlState = useCallback(() => {
    setAccountVideoRestrictionHint(ParentalControlManager.getTextForCurrentMaxAgeAllowedForVideo(ParentalControlKind.Account, true));
    setDeviceVideoRestrictionHint(ParentalControlManager.getTextForCurrentMaxAgeAllowedForVideo(ParentalControlKind.Device, true));
    setHasDeviceRestrictions(ParentalControlManager.hasDeviceRestrictions);
    setIsAccountLockPurchaseEnabled(ParentalControlManager.accountLockPurchase);
    setIsDeviceLockPurchaseEnabled(ParentalControlManager.deviceLockPurchase);
    setIsParentalControlEnabled(ParentalControlManager.isLocked());
  }, []);

  useEffect(() => {
    Messenger.on(MessengerEvents.PARENTAL_CONTROL_STATE_UPDATE, updateParentalControlState);
    Messenger.on(MessengerEvents.PROFILES_UPDATE, updateProfiles);
    updateParentalControlState();

    return () => {
      Messenger.off(MessengerEvents.PARENTAL_CONTROL_STATE_UPDATE, updateParentalControlState);
      Messenger.off(MessengerEvents.PROFILES_UPDATE, updateProfiles);
      abortController.current.abort('Component ParentalControlModal will unmount');
    };
  }, [updateParentalControlState, updateProfiles]);

  const handleParentalControlOnChanged = useCallback((checked: boolean, setting?: Setting) => {
    if (!setting) {
      return;
    }

    if (checked) {
      ParentalControlManager.lock();
    } else {
      ParentalControlManager.unlock();
    }
  }, []);

  const uploadAccountRestrictions = useCallback((parentalControlAndETag: ParentalControlAndETag): void => {
    const { signal } = abortController.current;
    const { eTag, parentalControl } = parentalControlAndETag;

    dispatch(sendParentalControlUpdateRequest(parentalControl, eTag, signal)).catch((error) => {
      Messenger.emit(MessengerEvents.NOTIFY_ERROR, `Error saving parental control restrictions: ${error.message}`);
    });
  }, []);

  const handleAccountLockPurchaseOnChanged = useCallback(
    (value: any, setting?: Setting) => {
      if (!setting) {
        return;
      }

      // Account: store in personal data
      ParentalControlManager.accountLockPurchase = Boolean(value) === true;
      uploadAccountRestrictions(ParentalControlManager.getAllRestrictions());
    },
    [uploadAccountRestrictions],
  );

  const handleDeviceLockPurchaseOnChanged = useCallback((value: any, setting?: Setting) => {
    if (!setting) {
      return;
    }

    // Device: store locally
    if (Boolean(value) === true) {
      ParentalControlManager.lockPurchaseForDevice();
    } else {
      ParentalControlManager.unlockPurchaseForDevice();
    }
  }, []);

  const handleLockUserProfilesManagementOnChanged = useCallback(
    async (checked: boolean) => {
      if (profiles === null) {
        return;
      }

      const newProfiles: Profiles = { ...profiles, lockUserProfilesManagement: checked };

      setProfiles(newProfiles);

      // Update in ProfilesManager
      await ProfilesManager.updateLockUserProfilesManagement(checked);
    },
    [profiles],
  );

  const handleAccountVideoSettingsOnClick = useCallback(() => {
    setCurrentRestrictionKind(ParentalControlKind.Account);
    setVisualState(ParentalControlModalState.Video);
  }, []);

  const handleDeviceVideoSettingsOnClick = useCallback(() => {
    setCurrentRestrictionKind(ParentalControlKind.Device);
    setVisualState(ParentalControlModalState.Video);
  }, []);

  const handleVideoSettingsOnClick = useCallback(() => {
    setVisualState(ParentalControlModalState.Video);
  }, []);

  const handleLockPurchaseOnChanged = useCallback(
    async (checked: boolean) => {
      if (currentProfile === null) {
        return;
      }

      const newProfile: Profile = { ...currentProfile, restrictions: { ...currentProfile.restrictions, lockPurchase: checked } };

      setCurrentProfile(newProfile);

      // Update existing profile
      await ProfilesManager.updateProfile(newProfile);
    },
    [currentProfile],
  );

  const profileDeleteConfirmationClosedCallback = useCallback(
    async (result: ConfirmationModalResult) => {
      if (result !== ConfirmationModalResult.Button2) {
        // Delete canceled
        return;
      }

      if (currentProfile !== null) {
        setIsWorking(true);
        await ProfilesManager.deleteProfile(currentProfile.id);
        setIsWorking(false);

        await updateProfiles();
      }
    },
    [currentProfile, updateProfiles],
  );

  const handleDeleteOnClick = useCallback(async () => {
    const data: CONFIRMATION_DATA_MODAL_TYPE = {
      button1Title: Localizer.localize('common.actions.cancel'),
      button2Title: Localizer.localize('common.actions.delete'),
      header: Localizer.localize('profiles.profile_editor.delete_profile.title'),
      question: Localizer.localize('profiles.profile_editor.delete_profile.question'),
    };

    const confirmationModalId = crypto.randomUUID();
    Messenger.once(`${MessengerEvents.MODAL_CLOSED}-${confirmationModalId}`, profileDeleteConfirmationClosedCallback);
    dispatch(showConfirmationModal(data, Localizer.localize('accessibility.modal.confirmation_delete_profile', { name: currentProfile?.name ?? '' }), confirmationModalId));
  }, [currentProfile, profileDeleteConfirmationClosedCallback]);

  const handleOnVideoMinAgeChanged = useCallback(
    async (minAge: number) => {
      if (currentProfile === null) {
        if (currentRestrictionKind === ParentalControlKind.Device) {
          // Device: store locally
          ParentalControlManager.setMaxAgeAllowedForVideo(currentRestrictionKind, minAge);
        } else {
          // Account: store in personal data
          uploadAccountRestrictions(ParentalControlManager.getAllRestrictionsWithNewMaxAgeAllowedForVideo(minAge));
        }
        return;
      }

      // Profile
      const newProfile: Profile = { ...currentProfile, restrictions: { ...currentProfile.restrictions, maxAgeAllowedForVideo: minAge } };

      setCurrentProfile(newProfile);

      // Update existing profile
      await ProfilesManager.updateProfile(newProfile);
    },
    [currentProfile, currentRestrictionKind, uploadAccountRestrictions],
  );

  const handleProfileOnChange = useCallback(async (newProfile: Profile) => {
    setCurrentProfile(newProfile);

    // Update existing profile
    await ProfilesManager.updateProfile(newProfile);
  }, []);

  const handleCreatePinCodeOnClick = useCallback(() => {
    dispatch(showPinModal({ kind: PinModalKind.CreateMaster }));
  }, []);

  const handleProfileOnClick = useCallback(
    (profileId: string) => {
      const profile = profiles?.list.find((profile) => profile.id === profileId);

      if (!profile) {
        return;
      }

      setCurrentProfile(profile);
      setVisualState(ParentalControlModalState.Profile);
    },
    [profiles],
  );

  const renderProfilesSection = useCallback((): React.Node => {
    if (!ProfilesManager.isEnabled) {
      return null;
    }

    return (
      <>
        <div className='pcSectionLabel'>{Localizer.localize('settings.parental_control.profiles_label')}</div>
        <div className='pcProfilesWrapper'>
          {profiles?.list.map((profile) => {
            const { avatarId, id, isKid, name } = profile;
            const isPrivate = isProfilePrivate(profile);

            let ariaLabelKey = 'accessibility.profiles.modify_restrictions';
            if (isKid) {
              ariaLabelKey = 'accessibility.profiles.modify_restrictions_kid';
            } else if (isPrivate) {
              ariaLabelKey = 'accessibility.profiles.modify_restrictions_private';
            }

            return (
              <RowSettingAction aria-label={Localizer.localize(ariaLabelKey, { name })} className='pcProfile' data={id} key={id} onClick={handleProfileOnClick}>
                <div className='pcProfileFullWidth'>
                  <Avatar avatarId={avatarId} displayName={DisplayNamePlacement.Right} isDisabled name={name} profileId={id} size={AvatarSize.Small} />
                  {isKid ? <PictoKid /> : null}
                  {isPrivate ? <PictoLock /> : null}
                </div>
              </RowSettingAction>
            );
          })}
        </div>
        <RowSettingToggle checked={profiles?.lockUserProfilesManagement ?? false} onChange={handleLockUserProfilesManagementOnChanged}>
          <div className='pcLabel'>
            <div>{Localizer.localize('settings.parental_control.lock_user_profiles_management_label')}</div>
            <div className='pcHint'>{Localizer.localize('settings.parental_control.lock_user_profiles_management_hint')}</div>
          </div>
        </RowSettingToggle>
      </>
    );
  }, [handleLockUserProfilesManagementOnChanged, handleProfileOnClick, profiles]);

  const handleSetDeviceRestrictionsOnClick = useCallback(() => {
    ParentalControlManager.setDeviceRestrictions();
  }, []);

  const handleDeleteDeviceRestrictionsOnClick = useCallback(() => {
    ParentalControlManager.deleteDeviceRestrictions();
  }, []);

  const renderDeviceRestrictionsSection = useCallback((): React.Node => {
    if (ProfilesManager.isEnabled) {
      return null;
    }

    if (hasDeviceRestrictions) {
      // Display current device restrictions, plus a DELETE button
      return (
        <>
          <div className='pcSectionLabel'>{Localizer.localize('settings.parental_control.device_section_label')}</div>
          <RowSettingAction onClick={handleDeviceVideoSettingsOnClick}>
            <div className='pcLabel row'>
              <div>{Localizer.localize('settings.parental_control.allowed_videos_label')}</div>
              <div className='pcSelectedValue'>{deviceVideoRestrictionHint}</div>
            </div>
          </RowSettingAction>
          {ParentalControlManager.hasLockPurchaseFeature ? (
            <RowSettingToggle checked={isDeviceLockPurchaseEnabled} onChange={handleDeviceLockPurchaseOnChanged} setting={Setting.PurchaseLockToggle}>
              <div className='pcLabel'>{Localizer.localize('settings.parental_control.lock_purchase_label')}</div>
            </RowSettingToggle>
          ) : null}
          <RowSettingAction onClick={handleDeleteDeviceRestrictionsOnClick}>
            <div className='pcLabel'>{Localizer.localize('settings.parental_control.device_delete_restrictions')}</div>
          </RowSettingAction>
        </>
      );
    }

    // Display a button allowing to configure device restrictions
    return (
      <>
        <div className='pcSectionLabel'>{Localizer.localize('settings.parental_control.device_section_label')}</div>
        <RowSettingAction onClick={handleSetDeviceRestrictionsOnClick}>
          <div className='pcLabel'>{Localizer.localize('settings.parental_control.device_set_restrictions')}</div>
          <div className='pcHint pcSectionHint'>{Localizer.localize('settings.parental_control.device_section_hint')}</div>
        </RowSettingAction>
      </>
    );
  }, [
    deviceVideoRestrictionHint,
    handleDeleteDeviceRestrictionsOnClick,
    handleDeviceLockPurchaseOnChanged,
    handleDeviceVideoSettingsOnClick,
    handleSetDeviceRestrictionsOnClick,
    hasDeviceRestrictions,
    isDeviceLockPurchaseEnabled,
  ]);

  const renderAccountRestrictionsSection = useCallback((): React.Node => {
    if (ProfilesManager.isEnabled) {
      return null;
    }

    return (
      <>
        <div className='pcSectionLabel'>{Localizer.localize('settings.parental_control.account_section_label')}</div>
        <RowSettingAction onClick={handleAccountVideoSettingsOnClick}>
          <div className='pcLabel row'>
            <div>{Localizer.localize('settings.parental_control.allowed_videos_label')}</div>
            <div className='pcSelectedValue'>{accountVideoRestrictionHint}</div>
          </div>
        </RowSettingAction>
        {ParentalControlManager.hasLockPurchaseFeature ? (
          <RowSettingToggle checked={isAccountLockPurchaseEnabled} onChange={handleAccountLockPurchaseOnChanged} setting={Setting.PurchaseLockToggle}>
            <div className='pcLabel'>{Localizer.localize('settings.parental_control.lock_purchase_label')}</div>
          </RowSettingToggle>
        ) : null}
      </>
    );
  }, [accountVideoRestrictionHint, handleAccountLockPurchaseOnChanged, handleAccountVideoSettingsOnClick]);

  const renderMainMenu = useCallback(
    (): React.Node => (
      <div className='pcSettingsWrapper'>
        <RowSettingToggle checked={isParentalControlEnabled} onChange={handleParentalControlOnChanged} setting={Setting.ParentalControlToggle}>
          <div className='pcLabel'>
            <div>{Localizer.localize('settings.parental_control.title')}</div>
            <div className='pcHint'>{Localizer.localize('settings.parental_control.hint')}</div>
          </div>
        </RowSettingToggle>
        {renderProfilesSection()}
        {renderDeviceRestrictionsSection()}
        {renderAccountRestrictionsSection()}
        <RowSettingAction aria-label={Localizer.localize('accessibility.modal.master_pin_reset_label')} onClick={handleCreatePinCodeOnClick}>
          <div>{Localizer.localize('settings.parental_control.reset_label')}</div>
        </RowSettingAction>
      </div>
    ),
    [
      handleCreatePinCodeOnClick,
      handleLockUserProfilesManagementOnChanged,
      handleParentalControlOnChanged,
      isParentalControlEnabled,
      renderAccountRestrictionsSection,
      renderDeviceRestrictionsSection,
      renderProfilesSection,
    ],
  );

  const renderProfile = useCallback((): React.Node => {
    if (currentProfile === null) {
      return null;
    }

    const { avatarId, id, isKid, name } = currentProfile;
    const isPrivate = isProfilePrivate(currentProfile);

    const videoRestrictionHint = ParentalControlManager.getShortTextFromMinAge(ProfilesManager.getMaxAgeAllowedForVideo(currentProfile));

    return (
      <div className='pcSettingsWrapper'>
        <div className='pcProfileRestrictionsHeader'>
          <Avatar avatarId={avatarId} isDisabled profileId={id} size={AvatarSize.Medium} />
          <div className='avatarNameWrapper'>
            {Localizer.localize('settings.parental_control.profile.profile_header', { name })}
            {isKid || isPrivate ? <ProfileTypeIndicator profileType={isKid ? ProfileType.Kid : ProfileType.Private} /> : null}
          </div>
        </div>
        <RowSettingAction onClick={handleVideoSettingsOnClick}>
          <div className='pcLabel row'>
            <div>{Localizer.localize('settings.parental_control.allowed_videos_label')}</div>
            <div className='pcSelectedValue'>{videoRestrictionHint}</div>
          </div>
        </RowSettingAction>
        {ParentalControlManager.hasLockPurchaseFeature ? (
          <RowSettingToggle checked={currentProfile.restrictions?.lockPurchase ?? false} onChange={handleLockPurchaseOnChanged}>
            <div className='pcLabel'>{Localizer.localize('settings.parental_control.lock_purchase_label')}</div>
          </RowSettingToggle>
        ) : null}
        <SharedProfileSettings canChangeProfileType onProfileChange={handleProfileOnChange} profile={currentProfile} />
        <RowSettingAction isDisabled={isWorking || currentProfile.isDefault} onClick={handleDeleteOnClick}>
          <div className='pcLabel'>{Localizer.localize('common.actions.delete')}</div>
          {currentProfile.isDefault ? <div className='pcHint'>{Localizer.localize('profiles.profile_editor.delete_profile.disabled_for_default')}</div> : null}
        </RowSettingAction>
      </div>
    );
  }, [currentProfile, handleDeleteOnClick, handleLockPurchaseOnChanged, handleProfileOnChange, handleVideoSettingsOnClick, isWorking]);

  const renderVideo = useCallback((): React.Node => {
    const initialMinAge = ProfilesManager.isEnabled ? ProfilesManager.getMaxAgeAllowedForVideo(currentProfile) : ParentalControlManager.getMaxAgeAllowedForVideo(currentRestrictionKind);

    return (
      <div className='pcVideoMinAgeSelection'>
        <div className='pcExplanations'>{Localizer.localize('settings.parental_control.choose_rating_videos')}</div>
        <RestrictionSelector initialMinAge={initialMinAge} items={ParentalControlManager.getRestrictionItems()} onMinAgeChange={handleOnVideoMinAgeChanged} />
      </div>
    );
  }, [currentProfile, currentRestrictionKind, handleOnVideoMinAgeChanged]);

  const renderGame = useCallback((): React.Node => {}, []);

  const renderModalContent = useCallback((): React.Node => {
    switch (visualState) {
      case ParentalControlModalState.Main:
        return renderMainMenu();

      case ParentalControlModalState.Profile:
        return renderProfile();

      case ParentalControlModalState.Video:
        return renderVideo();

      case ParentalControlModalState.Game:
        return renderGame();

      // No default
    }
  }, [renderGame, renderMainMenu, renderProfile, renderVideo, visualState]);

  const getModalBackCallback = useCallback(() => {
    switch (visualState) {
      case ParentalControlModalState.Game:
      case ParentalControlModalState.Video:
        return () => setVisualState(ProfilesManager.isEnabled ? ParentalControlModalState.Profile : ParentalControlModalState.Main);

      case ParentalControlModalState.Profile:
        return () => setVisualState(ParentalControlModalState.Main);

      case ParentalControlModalState.Main:
        return undefined;
    }
  }, [visualState]);

  const getModalHeader = useCallback(() => {
    switch (visualState) {
      case ParentalControlModalState.Game:
      case ParentalControlModalState.Video:
        return Localizer.localize('settings.parental_control.profile.videos_restrictions_header');

      case ParentalControlModalState.Profile:
        return null;

      case ParentalControlModalState.Main:
        return Localizer.localize('settings.parental_control.settings');
    }
  }, [visualState]);

  return (
    <Modal ariaLabel={Localizer.localize('accessibility.modal.parental_control')} backCallback={getModalBackCallback()} className='parentalControl' header={getModalHeader()}>
      <div className='parentalControlContent'>{renderModalContent()}</div>
    </Modal>
  );
};

export default ParentalControlModal;
